﻿import bemify from '../../utils/bemUtils';
import Component from '../component/component';
import Rest from '../../rest';

class AwardsCalculator extends Component {
    #makeModelsDropdownRef = null;
    #gallonsRef = null;
    #doubleAwardsRef = null;
    #tripleAwardRef = null;
    #bonusAwardsRef = null;
    #tripleAwardsRTE = null;
    #errorRef = null;
    #calcualteButtonRef = null;
    #waveLoaderRef = null;

    get title() {
        return this.getAttribute('title') ?? '';
    }
    get subtitle() {
        return this.getAttribute('subtitle') ?? '';
    }

    get tripleAwardsEnabled() {
        return this.getAttribute('tripleawards') ?? false;
    }

    get tripleAwardsRTE() {
        return this.getAttribute('data-triple-awards-rte') ?? '';
    }

    initTemplate() {
        const [block, element] = bemify('awards-calculator');
        const template = document.createElement('template');

        template.innerHTML = `
        <style>
            @import url('${process.env.APP_CSS_PATH}');
        </style>

        <sk-wave-loader id="awards-calculator__wave-loader"></sk-wave-loader>

        <div class="${block()}">
            ${this.#generateText()}

            <div class="${element('controls')}">
                <div class="form__group  ${element('calculate-make-model')}">
                    <sk-autocomplete id="awards-calculator__make-model"
                                     placeholder="Make & Model">
                    </sk-autocomplete>

                    <span class="form__required-message">Make & Model is required.</span>
                </div>

                <button id="awards-calculator__calculate-button" class="form__submit ${element('calculate-button')}" type="button">
                    Calculate
                </button>

                <div id="awards-calculator__gallons" class="${element('gallons')}">
                    Gallons required for:
                </div>

                <div id="awards-calculator__double-result" class="${element('result-left')}">
                </div>

                <div id="awards-calculator__bonus-result" class="${element('result-right')}">
                </div>

                <div id="awards-calculator__triple-awards-text" class="${element('result-row')}">
                </div>

                <div id="awards-calculator__triple-result" class="${element('result-center')}">
                </div>

                <div id="awards-calculator__error" class="${element('error')}">
                    Awards information not available for selected make and model.
                </div>
            </div>
        </div>
        `;

        const shadowRoot = this.attachShadow({ mode: 'open' });
        shadowRoot.appendChild(template.content.cloneNode(true));
    }

    #loadMakeModels = async () => {
        try {
            this.#waveLoaderRef.show = true;

            await Rest.get(`${process.env.API_MAKE_MODELS}/GetAll`).then(async data => {
                if (!data) return;

                const makeModels = await data.text();
                this.#makeModelsDropdownRef.items = makeModels;
            });
        } catch (error) {
            console.error(error);
        } finally {
            this.#waveLoaderRef.show = false;
        }
    };

    #clearFieldErrors(field) {
        const requiredMessage = field.parentNode.querySelector('.form__required-message--visible');
        const invalidMessage = field.parentNode.querySelector('.form__invalid-message--visible');

        if (requiredMessage) requiredMessage.classList.remove('form__required-message--visible');
        if (invalidMessage) invalidMessage.classList.remove('form__invalid-message--visible');
    };

    #clearErrors() {
        const requiredMessages = document.querySelectorAll('.form__required-message');

        for (let index = 0; index < requiredMessages?.length; ++index) {
            const requiredMessage = requiredMessages[index];

            if (requiredMessage) {
                requiredMessage.classList.remove('form__required-message--visible');
            }
        }

        const invalidMessages = document.querySelectorAll('.form__invalid-message');

        for (let index = 0; index < invalidMessages?.length; ++index) {
            const invalidMessage = invalidMessages[index];

            if (invalidMessage) {
                invalidMessage.classList.remove('form__invalid-message--visible');
            }
        }
    };

    #showErrorMessage(field, group, errorClass, errorClassVisible) {
        const errorMessage = group?.querySelector(`.${errorClass}`);

        if (errorMessage) {
            field.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'start'
            });
            field.focus();
            errorMessage.classList.add(errorClassVisible);
        }
    };

    #validate() {
        this.#clearErrors();

        let hasErrors = false;

        if (this.#makeModelsDropdownRef.value?.length <= 0 || this.#makeModelsDropdownRef.value === 'undefined') {
            hasErrors = true;
            this.#showErrorMessage(
                this.#makeModelsDropdownRef,
                this.#makeModelsDropdownRef.parentElement,
                'form__required-message',
                'form__required-message--visible'
            );
        }

        return !hasErrors;
    };

    #calculate = async () => {
        try {
            this.#makeModelsDropdownRef.onUnfocus();
            const id = this.#makeModelsDropdownRef.value;

            if (!this.#validate()) {
                return;
            }

            this.#waveLoaderRef.show = true;

            await Rest.get(`/api/GetAwardsModifiers?makeModelId=${id}`).then(async data => {
                if (!data) return;

                const modifiers = await data.json();
                let hasModifiers = false;

                if (modifiers.DoubleAwardsMinGals) {
                    hasModifiers = true;

                    this.#doubleAwardsRef.textContent = `Double Awards: ${modifiers.DoubleAwardsMinGals}`;
                    this.#doubleAwardsRef.classList.add('awards-calculator__result--visible');
                } else {
                    this.#doubleAwardsRef.classList.remove('awards-calculator__result--visible' );
                }

                //This needs to be both enabled in the CMS and have a modifier
                if (modifiers.TripleAwardsMinGals && this.tripleAwardsEnabled) {
                    hasModifiers = true;

                    this.#tripleAwardsRTE.textContent = `${this.tripleAwardsRTE}`;
                    this.#tripleAwardsRTE.classList.add('awards-calculator__result-row--visible');

                    this.#tripleAwardRef.textContent = `Triple Awards: ${modifiers.TripleAwardsMinGals}`;
                    this.#tripleAwardRef.classList.add('awards-calculator__result--visible');
                } else {
                    this.#tripleAwardRef.classList.remove('awards-calculator__result--visible');
                    this.#tripleAwardsRTE.classList.remove('awards-calculator__result-row--visible');
                }

                if (modifiers.BonusAwardsMinGals && modifiers.BonusAwardsMinGals.length > 0 && modifiers.BonusAwardsMinGals[0].MinGallons) {
                    hasModifiers = true;

                    this.#bonusAwardsRef.textContent = `Bonus Awards: ${modifiers.BonusAwardsMinGals[0].MinGallons}`;
                    this.#bonusAwardsRef.classList.add('awards-calculator__result--visible');
                } else {
                    this.#bonusAwardsRef.classList.remove('awards-calculator__result--visible');
                }

                //Center double/bonus awards if they are the only one for that row
                if (modifiers.DoubleAwardsMinGals && !(modifiers.BonusAwardsMinGals && modifiers.BonusAwardsMinGals.length > 0 && modifiers.BonusAwardsMinGals[0].MinGallons)) {
                    this.#doubleAwardsRef.classList.remove('awards-calculator__result-left');
                    this.#doubleAwardsRef.classList.add('awards-calculator__result-center');
                } else if ((modifiers.BonusAwardsMinGals && modifiers.BonusAwardsMinGals.length > 0 && modifiers.BonusAwardsMinGals[0].MinGallons) && !modifiers.DoubleAwardsMinGals) {
                    this.#bonusAwardsRef.classList.remove('awards-calculator__result-right');
                    this.#bonusAwardsRef.classList.add('awards-calculator__result-center');

                } else {
                    this.#doubleAwardsRef.classList.remove('awards-calculator__result-center');
                    this.#doubleAwardsRef.classList.add('awards-calculator__result-left');

                    this.#bonusAwardsRef.classList.remove('awards-calculator__result-center');
                    this.#bonusAwardsRef.classList.add('awards-calculator__result-right');
                }


                if (hasModifiers) {
                    this.#errorRef.classList.remove('awards-calculator__error--visible');
                    this.#gallonsRef.classList.add('awards-calculator__gallons--visible');
                } else {
                    this.#errorRef.classList.add('awards-calculator__error--visible');
                    this.#gallonsRef.classList.remove('awards-calculator__gallons--visible');
                }
            });
        } catch (error) {
            console.error(error);
        } finally {
            this.#waveLoaderRef.show = false;
        }
    };

    #clearResults = () => {
        this.#doubleAwardsRef.classList.remove('awards-calculator__result--visible');
        this.#bonusAwardsRef.classList.remove('awards-calculator__result--visible');
        this.#tripleAwardRef.classList.remove('awards-calculator__result--visible');
        this.#tripleAwardsRTE.classList.remove('awards-calculator__result-row--visible');
        this.#errorRef.classList.remove('awards-calculator__error--visible');
        this.#gallonsRef.classList.remove('awards-calculator__gallons--visible');
    };

    #generateText() {
        if (!this.title && !this.subtitle) return '';

        const [_, element] = bemify('awards-calculator');

        return `
            <div class="${element('row')}">
                <div class="${element('col')}">
                    ${this.title ? `<span class="${element('title')}">${this.title}</span>` : ''}
                    ${this.subtitle ? `<span class="${element('sub-title')}">${this.subtitle}</span>` : ''}
                </div>
            </div>
        `;
    }

    onMounted() {
        this.#makeModelsDropdownRef = this.shadowRoot.getElementById('awards-calculator__make-model');
        this.#doubleAwardsRef = this.shadowRoot.getElementById('awards-calculator__double-result');
        this.#tripleAwardRef = this.shadowRoot.getElementById('awards-calculator__triple-result');
        this.#bonusAwardsRef = this.shadowRoot.getElementById('awards-calculator__bonus-result');
        this.#tripleAwardsRTE = this.shadowRoot.getElementById('awards-calculator__triple-awards-text');
        this.#gallonsRef = this.shadowRoot.getElementById('awards-calculator__gallons');
        this.#errorRef = this.shadowRoot.getElementById('awards-calculator__error');
        this.#calcualteButtonRef = this.shadowRoot.getElementById('awards-calculator__calculate-button');
        this.#waveLoaderRef = this.shadowRoot.getElementById('awards-calculator__wave-loader');

        this.#makeModelsDropdownRef.addEventListener('on-change', ({ detail }) => {
            this.#clearResults();

            this.#makeModelsDropdownRef.value = detail.value;
            if (!detail.value || !this.#makeModelsDropdownRef.items.includes(`"${detail.value}"`)) return;

            this.#clearFieldErrors(this.#makeModelsDropdownRef);
        });
        this.#calcualteButtonRef.addEventListener('click', async () => await this.#calculate());

        this.#loadMakeModels();
    }

    render() {
    }
}

customElements.define('sk-awards-calculator', AwardsCalculator);

export default AwardsCalculator;